import { ChangeDetectionStrategy, Component, computed, effect, Input, input, InputSignal, ViewChild } from '@angular/core';
import { ApexAxisChartSeries, ApexChart, ApexXAxis, ApexDataLabels, ApexPlotOptions, ApexLegend, ApexYAxis, ApexTooltip, ApexFill, NgApexchartsModule, ChartComponent } from 'ng-apexcharts';
import { BaseDashboardChartComponent } from '../base-dashboard-chart.component';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { ChartCardComponent } from '../chart-card/chart-card.component';
import { TableModule } from 'primeng/table';
import { SpinnerComponent } from '../../spinner/spinner.component';
import { TranslateService } from '@ngx-translate/core';
import {TranslatePipe} from "@ngx-translate/core";
import { Subscription } from 'rxjs';
export type ChartOptionsStacked = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  responsive: ApexResponsive[];
  xaxis: ApexXAxis;
  yaxis: ApexYAxis | ApexYAxis[];
  legend: ApexLegend;
  fill: ApexFill;
};
@Component({
  selector: 'co-stacked-bar',
  imports: [NgApexchartsModule, SelectModule, FormsModule, ChartCardComponent, TableModule, SpinnerComponent, TranslatePipe] ,
  templateUrl: './stacked-bar.component.html',
  styleUrl: './stacked-bar.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StackedBarComponent extends BaseDashboardChartComponent{

  @ViewChild('chart') chart?: ChartComponent;
  public chartOptions: Partial<ChartOptionsStacked> | any;
  data = input.required<StackedBarData>();
  chartData = computed(() => this.data().chartData);
  tableData = computed(() => this.data().tableData ?? []);
  chartReady = input.required<boolean>();

  categoryKeys = computed(() => {
    const rows = this.tableData();
    const keySet = new Set<string>();

    for (const row of rows) {
      Object.keys(row.categoryExpenses).forEach(key => keySet.add(key));
    }

    return Array.from(keySet).sort(); // Sort for consistent order
  });

  // chartData: InputSignal<StackedBarChartData[]> = input.required();
  // tableData: InputSignal<StackedBarTableData[]> = input.required();
  
  private categoryColors: string[]  = [
    '#d62728',
    '#ff9896',
    '#ff7f0e',
    '#ffbb78'
  ];
  
  private langChangeSub?: Subscription;
  constructor(private translate: TranslateService) {
    super();
     this.langChangeSub = this.translate.onLangChange.subscribe(() => {
    this.prepareChart();
  });
    effect(() => {
      this.prepareChart();
    });
  }

  private prepareChart() {
    const dataList = this.chartData() ?? [];

    const allCategories =  Array.from(
      new Set(dataList.flatMap(d => Object.keys(d.categoryPercentages)))
    ).sort();

    const series = allCategories.map(category => ({
      name: category,
      data: dataList.map(d => d.categoryPercentages[category] || 0)
    }));

    const xAxisLabels = dataList.map(d => this.wrapAxisLabel(d.groupingKey));

    this.chartOptions = {
      series,
      chart: {
        type: 'bar',
        height: 400,
        stacked: true,
        toolbar: { show: true },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          stacked: true
        },
      },
      colors: this.categoryColors.slice(0, allCategories.length),
      dataLabels: {
        enabled: true,
        formatter: (val: number, opts: { w: { globals: { stackedSeriesTotals: { [x: string]: any; }; }; }; dataPointIndex: string | number; }) => {
          const total = opts.w.globals.stackedSeriesTotals[opts.dataPointIndex];
          return total > 0 ? `${((val / total) * 100).toFixed(1)}%` : '0%';
        },
      },
      legend: {
        show: true,
        position: 'right',
      },
      xaxis: {
        categories: xAxisLabels,
        labels: {
          rotate: -45,
          rotateAlways: true,
          trim: false,
          maxHeight: 120,
          style: {
            fontSize: '9px',
          },
        },
      },
      yaxis: {
        title: { text: this.translate.instant('STACKED_BAR.PERCENTAGE') },
        labels:{
          formatter: function (value: number) {
            return value.toFixed(0) + '%';
          }
        },
        max: 100
      },
      tooltip: {
        y: {
          formatter: (val: number, opts: { w: { globals: { stackedSeriesTotals: { [x: string]: any; }; }; }; dataPointIndex: string | number; }) => {
          const total = opts.w.globals.stackedSeriesTotals[opts.dataPointIndex];
          return total > 0 ? `${((val / total) * 100).toFixed(1)}%` : '0%';
        },
        },
      },
      fill: {
        opacity: 1
      },
    };
  }

  private wrapAxisLabel(label: string): string | string[] {
    const trimmedLabel = label?.trim() ?? '';
    const hyphenIndex = trimmedLabel.indexOf('-');

    if (hyphenIndex > 0 && hyphenIndex < trimmedLabel.length - 1) {
      const beforeHyphen = trimmedLabel.slice(0, hyphenIndex).trim();
      const afterHyphen = trimmedLabel.slice(hyphenIndex + 1).trim();

      return [`${beforeHyphen} -`, afterHyphen];
    }

    const words = trimmedLabel.split(/\s+/).filter(Boolean);
    if (words.length < 2) return trimmedLabel;

    const splitIndex = Math.ceil(words.length / 2);
    return [
      words.slice(0, splitIndex).join(' '),
      words.slice(splitIndex).join(' ')
    ];
  }
}

export interface StackedBarData {
  chartData: StackedBarChartData[];
  tableData: StackedBarTableData[];
}

export interface StackedBarChartData {
  groupingKey: string;
  categoryAmounts: { [key: string]: number };
  categoryPercentages: { [key: string]: number };
}

export interface StackedBarTableData {
  groupingKey: string;
  categoryExpenses: { [key: string]: number };
  total: number;
}
